// frontend/src/pages/BlogPostPage/BlogPostPage.jsx

import React, { useEffect, useCallback, useContext } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Paper, Avatar, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { useQuery } from '@tanstack/react-query';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import '../BlogIndexPage/BlogPages.css'; // Use the new shared stylesheet
import { AuthContext } from '../../contexts/AuthContext';

const BlogPostPage = () => {
    const { t } = useTranslation();
    const { slug } = useParams();
    const { fetchWithAuth } = useContext(AuthContext);

    const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);

    const particlesConfig = {
        background: { color: { value: 'transparent' } },
        fpsLimit: 120,
        interactivity: { events: { onHover: { enable: true, mode: 'repulse' }, resize: true }, modes: { repulse: { distance: 100, duration: 0.4 } } },
        particles: {
            color: { value: '#ffffff' },
            links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.4, width: 1 },
            move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 1, straight: false },
            number: { density: { enable: true, area: 1000 }, value: 50 }, // Less dense for text-heavy pages
            opacity: { value: 0.5 },
            shape: { type: 'circle' },
            size: { value: { min: 1, max: 3 } },
        },
        detectRetina: true,
    };

    // TanStack Query with automatic 1-hour cache
    const { 
        data: post, 
        isLoading, 
        error: queryError 
    } = useQuery({
        queryKey: ['blogPost', slug],
        queryFn: async () => {
            const response = await fetchWithAuth(`/api/blog/posts/${slug}`);
            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error(t('blog.post.notFound'));
                }
                throw new Error(t('blog.post.fetchFailed'));
            }
            return response.json();
        },
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const error = queryError?.message || null;

    // Scroll to top on slug change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const renderContent = () => {
        if (isLoading) return (
            <LoadingIndicator 
                messages={{
                    general: { key: 'blog.post.loading' }
                }} 
            />
        );
        if (error) return <ErrorMessage message={error} />;
        if (!post) return null;

        return (
            <>
                <Button component={RouterLink} to="/blog" variant="outlined" startIcon={<ArrowBackIcon />} className="back-to-blog-btn">
                    {t('blog.post.backToIndex')}
                </Button>
                <Typography variant="h3" component="h1" className="post-title">{post.metadata.title}</Typography>
                <Box className="post-meta-container">
                    <Avatar className="author-avatar">{post.metadata.author.charAt(0)}</Avatar>
                    <Box>
                        <Typography variant="subtitle1" component="p" className="author-name">{post.metadata.author}</Typography>
                        <Typography variant="body2" className="post-date">{formatDate(post.metadata.date)}</Typography>
                    </Box>
                </Box>
                <Paper elevation={0} className="post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
            </>
        );
    };

    return (
        <div className="blog-page-container">
            <Particles id="tsparticles-blog" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{isLoading ? t('blog.helmet.indexTitle') : t('blog.post.helmet.title', { postTitle: post?.metadata.title })}</title>
                {!isLoading && post && <meta name="description" content={post.metadata.summary} />}
            </Helmet>
            
            <div className="blog-nav-container">
                <UniversalNav />
            </div>

            <div className="blog-content-wrapper">
                <Box className="blog-glass-container">
                    {renderContent()}
                </Box>
            </div>
        </div>
    );
};

export default BlogPostPage;